// Shared typed read model for the purchase/sales invoice workflow editor.
//
// Single home for the draft and approved shapes edited by both directions. Purchase and sales share this model; only the commercial
// pair differs (purchaseQuantity/purchaseUnit vs saleQuantity/saleUnit).
// Server-persisted documents (APPROVED/CANCELLED/UNKNOWN) render read-only.
//
// Unknown stays unknown: every money/rate/quantity field is nullable and the
// normalizers never fabricate zero, FX, totals or a physical unit. No network
// calls here.
import { formatUnit } from '../utils/units';
import type { QuantityUnit } from '../utils/commercialQuantity';

export type WorkflowInvoiceDirection = 'PURCHASE' | 'SALES';

/** STANDARD generates stock movements on approval; others never do. */
export type WorkflowInvoiceKind = 'STANDARD' | 'CORRECTION' | 'ADVANCE' | 'PROFORMA' | 'UNKNOWN';

/**
 * DRAFT/EXTRACTED/READY are editable working states. APPROVED and CANCELLED
 * are server-persisted snapshots; UNKNOWN is a fail-closed read-only bucket
 * for any server status this frontend does not recognize — it must never
 * degrade into an editable DRAFT.
 */
export type WorkflowInvoiceStatus =
  | 'DRAFT'
  | 'EXTRACTED'
  | 'READY'
  | 'APPROVED'
  | 'CANCELLED'
  | 'UNKNOWN';

export type WorkflowExtractionStatus = 'IDLE' | 'PENDING' | 'SUCCESS' | 'STALE' | 'FAILED';

/**
 * Server-owned original OCR review context from
 * `sourceSnapshot.extractionReview`. `documentTotal` is the printed
 * unspecified TOTAL (never automatically net or gross) in the immutable
 * original `currency` captured at extraction time; `notes` are bounded
 * Polish uncertainty hints. Display-only: never an editable financial total
 * and never copied into submitted invoice monetary fields.
 */
export interface WorkflowInvoiceExtractionReview {
  documentTotal: number | null;
  /** Original OCR currency; null while unknown (never the live form currency). */
  currency: string | null;
  notes: string[];
}

export interface WorkflowInvoiceLine {
  key: string;
  /** Server identity; absent for not-yet-persisted editor lines. */
  id?: string | null;
  lineNumber?: number | null;
  productVariantId?: string | null;
  productName?: string | null;
  /** Explicit match to a source order line; required before approval. */
  sourceOrderLineId?: string | null;
  matchConfirmed: boolean;
  /** Entered physical amount; null while unknown (never a zero default). */
  unit?: QuantityUnit | null;
  quantity?: number | null;
  /** Canonical derivable set for the entered amount (pcs/m2/m3). */
  quantityPcs?: number | null;
  quantityM2?: number | null;
  quantityM3?: number | null;
  conversionUnit?: QuantityUnit | null;
  conversionQuantity?: number | null;
  /** Canonical catalog geometry feeding the quantity conversion. */
  width?: number | null;
  height?: number | null;
  length?: number | null;
  thickness?: number | null;
  /** Independent commercial price basis (purchase or sales side). */
  commercialQuantity?: number | null;
  commercialUnit?: QuantityUnit | null;
  unitPrice?: number | null;
  taxRate?: number | null;
  netAmount?: number | null;
  vatAmount?: number | null;
  grossAmount?: number | null;
  currency?: string | null;
  quantityRaw?: number | null;
  quantityUnitRaw?: string | null;
  discountPercent?: number | null;
  discountAmount?: number | null;
}

/**
 * One selectable source order line supplied by the parent domain query.
 * Options render business labels (order number + product + remaining
 * quantity); IDs travel as values only, never as typed input.
 */
export interface WorkflowInvoiceSourceLineCandidate {
  sourceOrderLineId: string;
  sourceOrderId?: string;
  orderNumber: string;
  productName: string;
  productVariantId?: string | null;
  variantLabel?: string | null;
  remainingQuantity?: number | null;
  remainingUnit?: QuantityUnit | null;
  width?: number | null;
  height?: number | null;
  length?: number | null;
  thickness?: number | null;
  /**
   * Persisted physical quantity set of the source line (szt./m²/m³). An invoice
   * line matched to this source may only carry units present here; the server
   * scales the source snapshot and never backfills a persisted NULL from
   * catalog geometry (`normalizeLineQuantitySet`). Absent = unknown (legacy).
   */
  sourceQuantitySet?: { pcs: number | null; m2: number | null; m3: number | null } | null;
}

export interface WorkflowInvoice {
  /** Server identity; absent for not-yet-persisted editor drafts. */
  id?: string | null;
  /** Optimistic-lock version for draft updates; null until first persist. */
  version?: number | null;
  direction: WorkflowInvoiceDirection;
  kind: WorkflowInvoiceKind;
  status: WorkflowInvoiceStatus;
  extractionStatus: WorkflowExtractionStatus;
  extractionError?: string | null;
  extractionModel?: string | null;
  /** Original OCR review context; absent for rows without an extraction. */
  extractionReview?: WorkflowInvoiceExtractionReview | null;
  /** Original number printed on the received document; editable, never generated. */
  originalDocumentNumber?: string | null;
  sellerName?: string | null;
  sellerTaxId?: string | null;
  buyerName?: string | null;
  buyerTaxId?: string | null;
  issueDate?: string | null;
  dueDate?: string | null;
  currency?: string | null;
  /** Unknown stays null; the editor never defaults FX. */
  exchangeRateToPLN?: number | null;
  exchangeRateDate?: string | null;
  exchangeRateSnapshot?: { rate: number; source: string; requestedDate: string; effectiveDate: string; tableNumber: string; currency?: string } | null;
  documentNotes?: string | null;
  totalNet?: number | null;
  totalVat?: number | null;
  totalGross?: number | null;
  settlementPeriod?: string | null;
  supplierOrderId?: string | null;
  supplierId?: string | null;
  /** Customer order link for sales drafts (backend `orderId`). */
  orderId?: string | null;
  correctsInvoiceId?: string | null;
  coverageState?: 'PARTIAL' | 'FULL' | null;
  lines: WorkflowInvoiceLine[];
  linesLoaded: boolean;
  manuallyCorrected: boolean;
  approvedAt?: string | null;
  approvedBy?: string | null;
  sourceFileName?: string | null;
  approvalResult?: { pzDocuments: Array<{ id: string; pzNumber: string; sourceSupplierOrderId: string }>; orders: Array<{ id: string; commerciallyFrozen: boolean; coverageState: 'PARTIAL' | 'FULL' }>; replayed: boolean } | null;
  sourceFiles?: Array<{ name: string; sha256: string }>;
  extractionSeq?: number | null;
  lastAppliedExtractionSeq?: number | null;
  sellerAddress?: string | null;
  buyerAddress?: string | null;
  orderReference?: string | null;
  paymentTerms?: string | null;
}

/** Editable draft the editor owns; persisted snapshots are read-only. */
export type WorkflowInvoiceDraft = WorkflowInvoice;

export function isStockGeneratingKind(kind: WorkflowInvoiceKind): boolean {
  return kind === 'STANDARD';
}

export function isApprovedSnapshot(invoice: Pick<WorkflowInvoice, 'status'>): boolean {
  return invoice.status === 'APPROVED';
}

/** Only working states are editable; everything persisted is read-only. */
export function isEditableWorkflowStatus(status: WorkflowInvoiceStatus): boolean {
  return status === 'DRAFT' || status === 'EXTRACTED' || status === 'READY';
}

/** Business label for a source-line candidate option; IDs stay values. */
export function formatSourceLineCandidateLabel(
  candidate: WorkflowInvoiceSourceLineCandidate,
): string {
  const parts = [candidate.orderNumber, candidate.productName];
  if (candidate.variantLabel) {
    parts.push(candidate.variantLabel);
  }
  if (
    candidate.remainingQuantity !== null &&
    candidate.remainingQuantity !== undefined &&
    candidate.remainingUnit
  ) {
    parts.push(`${candidate.remainingQuantity} ${formatUnit(candidate.remainingUnit)}`);
  }
  return parts.filter(Boolean).join(' — ');
}
